import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { Integration } from "../../../types/integration";
import type { ChannelConnection } from "../../../types/channel";
import { MESSAGE_CHANNELS, type MessageChannelDef } from "../../../config/messageChannels";
import { Logo } from "../../../components/dashboard/Logo";
import { StatusPill } from "../../../components/dashboard/StatusPill";
import { MsTeamsSetupModal } from "../../../components/dashboard/integrations/MsTeamsSetupModal";
import { TelegramSetupModal } from "../../../components/dashboard/integrations/TelegramSetupModal";
import { WhatsAppSetupModal } from "../../../components/dashboard/integrations/WhatsAppSetupModal";
import styles from "./MessageChannels.module.css";

// The section title + subtitle, shared by the collapsed bar and the expanded
// grid so the heading reads identically across both states.
const SECTION_TITLE = "Conveniently talk to your agent.";
const SECTION_SUBTITLE =
  "Channels are how you message your agent. Set up the ready ones yourself in a couple of minutes, or ask us to wire up the rest.";

type Props = {
  channels: ReadonlyArray<ChannelConnection>;
  // The tenant's integration catalog, used only to borrow brand logos by slug.
  integrations: ReadonlyArray<Integration>;
  tenantId: string | null;
  // Why self-serve setup can't run yet (shown on the disabled button), or
  // null when it can: it needs a running, unlocked instance.
  setupBlockedReason: string | null;
  // Once a channel is connected the section is demoted out of sight: it renders
  // a slim "you're connected, set up another" bar that expands to the full grid.
  collapsible?: boolean;
  // Record / withdraw an access request for a non-self-serve channel.
  onRequest: (channel: string) => Promise<boolean>;
  onCancelRequest: (channel: string) => Promise<boolean>;
  // Refetch channel state after a guided setup completes.
  onChanged: () => void;
  // Whether the viewer owns the instance. Linking WhatsApp runs as the owner,
  // so only they can set it up or reconnect it.
  canLink?: boolean;
  // A channel to open straight into reconnect (the banner's button, or a
  // `?reconnect=whatsapp` link from support). Cleared via onReconnectHandled.
  reconnectRequest?: string | null;
  onReconnectHandled?: () => void;
  // Shown between the heading and the cards (the "WhatsApp unlinked your
  // agent" banner), so it sits right above the card it is about.
  notice?: ReactNode;
};

export const MessageChannelsSection = ({
  channels,
  integrations,
  tenantId,
  setupBlockedReason,
  collapsible = false,
  onRequest,
  onCancelRequest,
  onChanged,
  canLink = true,
  reconnectRequest = null,
  onReconnectHandled,
  notice = null,
}: Props): JSX.Element | null => {
  const [openSetup, setOpenSetup] = useState<"whatsapp" | "telegram" | "msteams" | null>(null);
  const [whatsappMode, setWhatsappMode] = useState<"setup" | "reconnect">("setup");
  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (reconnectRequest !== "whatsapp" || tenantId === null) return;
    setWhatsappMode("reconnect");
    setOpenSetup("whatsapp");
    setExpanded(true);
    onReconnectHandled?.();
  }, [reconnectRequest, tenantId, onReconnectHandled]);

  const stateBySlug = useMemo(
    () => new Map(channels.map((c) => [c.channel, c])),
    [channels],
  );
  const integrationBySlug = useMemo(
    () => new Map(integrations.map((i) => [i.slug, i])),
    [integrations],
  );

  const connectedDefs = useMemo(
    () => MESSAGE_CHANNELS.filter((d) => stateBySlug.get(d.slug)?.status === "active"),
    [stateBySlug],
  );

  if (tenantId === null) return null;

  const setupTitle =
    openSetup === "whatsapp" ? "WhatsApp" : openSetup === "msteams" ? "Microsoft Teams" : "Telegram";
  // The channel slug and the setup key differ for Teams (slug
  // "microsoft-teams", setup "msteams"); map through the catalog def.
  const setupSlug = MESSAGE_CHANNELS.find((d) => d.setup === openSetup)?.slug ?? openSetup;
  const setupStatus = setupSlug !== null ? stateBySlug.get(setupSlug)?.status : undefined;
  const setupConnected = setupStatus === "active";

  const modals = (
    <>
      {openSetup === "whatsapp" ? (
        <WhatsAppSetupModal
          open
          tenantId={tenantId}
          title={setupTitle}
          mode={whatsappMode}
          alreadyConnected={setupConnected}
          canLink={canLink}
          onClose={() => setOpenSetup(null)}
          onChanged={onChanged}
        />
      ) : null}

      {openSetup === "telegram" ? (
        <TelegramSetupModal
          open
          tenantId={tenantId}
          title={setupTitle}
          // 'pending' = bot wired but no device paired yet, so the skip-ahead
          // path (a bot already exists) applies there too.
          alreadyConnected={setupConnected || setupStatus === "pending"}
          onClose={() => setOpenSetup(null)}
          onChanged={onChanged}
        />
      ) : null}

      {openSetup === "msteams" ? (
        <MsTeamsSetupModal
          open
          tenantId={tenantId}
          title={setupTitle}
          onClose={() => setOpenSetup(null)}
          onChanged={onChanged}
        />
      ) : null}
    </>
  );

  // Demoted state: a quiet bar that expands to reveal the grid. It keeps the
  // same section header (eyebrow + title) as every other dashboard section so
  // the section reads consistently; only the verbose tagline + the channel grid
  // are deferred until the user expands it.
  if (collapsible && !expanded) {
    const names = connectedDefs.map((d) => d.name);
    const summary =
      names.length > 0 ? `You're connected on ${joinNames(names)}.` : "Manage your message channels.";
    return (
      <section className={styles.section} aria-labelledby="dashboard-channels-title">
        <div className={styles.header}>
          <span className={styles.eyebrow}>// MESSAGE CHANNELS</span>
          <h2 className={styles.title} id="dashboard-channels-title">
            {SECTION_TITLE}
          </h2>
          <p className={styles.subtitle}>{SECTION_SUBTITLE}</p>
        </div>
        <button
          type="button"
          className={styles.collapsedBar}
          data-testid="channels-collapsed"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
        >
          <span className={styles.collapsedLogos}>
            {connectedDefs.map((d) => (
              <Logo
                key={d.slug}
                slug={d.slug}
                size={26}
                variant="brand"
                brandHex={integrationBySlug.get(d.slug)?.iconBrandHex ?? null}
              />
            ))}
          </span>
          <span className={styles.collapsedSummary}>{summary}</span>
          <span className={styles.collapsedAction}>Set up another</span>
        </button>
        {modals}
      </section>
    );
  }

  return (
    <section className={styles.section} aria-labelledby="dashboard-channels-title">
      <div className={styles.header}>
        <span className={styles.eyebrow}>// MESSAGE CHANNELS</span>
        <div className={styles.headerRow}>
          <h2 className={styles.title} id="dashboard-channels-title">
            {SECTION_TITLE}
          </h2>
          {collapsible ? (
            <button
              type="button"
              className={styles.hideBtn}
              data-testid="channels-hide"
              onClick={() => setExpanded(false)}
            >
              Hide
            </button>
          ) : null}
        </div>
        <p className={styles.subtitle}>{SECTION_SUBTITLE}</p>
      </div>

      {notice}

      <div className={styles.grid}>
        {MESSAGE_CHANNELS.map((def) => (
          <ChannelCard
            key={def.slug}
            def={def}
            state={stateBySlug.get(def.slug) ?? null}
            integration={integrationBySlug.get(def.slug) ?? null}
            setupBlockedReason={setupBlockedReason}
            canLink={canLink}
            onSetup={() => {
              setWhatsappMode("setup");
              setOpenSetup(def.setup ?? null);
            }}
            onReconnect={() => {
              setWhatsappMode("reconnect");
              setOpenSetup(def.setup ?? null);
            }}
            onRequest={() => onRequest(def.slug)}
            onCancelRequest={() => onCancelRequest(def.slug)}
          />
        ))}
      </div>

      {modals}
    </section>
  );
};

const joinNames = (names: ReadonlyArray<string>): string => {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

type CardProps = {
  def: MessageChannelDef;
  state: ChannelConnection | null;
  integration: Integration | null;
  setupBlockedReason: string | null;
  canLink: boolean;
  onSetup: () => void;
  onReconnect: () => void;
  onRequest: () => Promise<boolean>;
  onCancelRequest: () => Promise<boolean>;
};

const ChannelCard = ({
  def,
  state,
  integration,
  setupBlockedReason,
  canLink,
  onSetup,
  onReconnect,
  onRequest,
  onCancelRequest,
}: CardProps): JSX.Element => {
  const connected = state?.status === "active";
  const requested = state?.status === "requested";
  const needsReconnect = connected && state?.needsReconnect === true;
  // Only WhatsApp links as the owner; Telegram and Teams setup stay open to
  // anyone who can manage the instance.
  const linkBlockedReason =
    def.setup === "whatsapp" && !canLink ? "Only the agent's owner can link WhatsApp." : null;

  return (
    <div className={styles.card} data-testid={`channel-${def.slug}`}>
      <header className={styles.cardHead}>
        <span className={styles.logoChip}>
          <Logo
            slug={def.slug}
            size={40}
            variant="brand"
            brandHex={integration?.iconBrandHex ?? null}
          />
        </span>
        {renderBadge(def, connected, requested, needsReconnect)}
      </header>

      <div className={styles.cardBody}>
        <span className={styles.name}>{def.name}</span>
        <p className={styles.blurb}>{def.description}</p>
      </div>

      <div className={styles.cardFooter}>
        {renderAction(def, {
          connected,
          requested,
          needsReconnect,
          setupBlockedReason,
          linkBlockedReason,
          onSetup,
          onReconnect,
          onRequest,
          onCancelRequest,
        })}
      </div>
    </div>
  );
};

// Status lives in the header (like the integration cards) so the footer can be
// the action alone — no more cramped "Requested" colliding with the button.
const renderBadge = (
  def: MessageChannelDef,
  connected: boolean,
  requested: boolean,
  needsReconnect: boolean,
): JSX.Element | null => {
  if (needsReconnect) return <StatusPill variant="warning">Disconnected</StatusPill>;
  if (connected) return <StatusPill variant="active">Connected</StatusPill>;
  if (requested) return <StatusPill variant="available">Requested</StatusPill>;
  if (def.recommended) return <span className={styles.recommended}>Recommended</span>;
  return null;
};

type ActionState = {
  connected: boolean;
  requested: boolean;
  needsReconnect: boolean;
  setupBlockedReason: string | null;
  // Why this viewer can't link (not the owner), or null when they can.
  linkBlockedReason: string | null;
  onSetup: () => void;
  onReconnect: () => void;
  onRequest: () => Promise<boolean>;
  onCancelRequest: () => Promise<boolean>;
};

const renderAction = (def: MessageChannelDef, s: ActionState): JSX.Element | null => {
  if (def.mode === "self-serve") {
    if (s.needsReconnect) {
      const blocked = s.setupBlockedReason ?? s.linkBlockedReason;
      return (
        <button
          type="button"
          className={styles.btnPrimary}
          data-testid={`channel-reconnect-${def.slug}`}
          onClick={s.onReconnect}
          disabled={blocked !== null}
          title={blocked ?? undefined}
        >
          Reconnect
        </button>
      );
    }
    // Manage stays open to collaborators: pairing their own number on an agent
    // that is already linked needs no owner rights.
    const blocked = s.setupBlockedReason ?? (s.connected ? null : s.linkBlockedReason);
    return (
      <button
        type="button"
        className={s.connected ? styles.btnGhost : styles.btnPrimary}
        data-testid={`channel-action-${def.slug}`}
        onClick={s.onSetup}
        disabled={blocked !== null}
        title={blocked ?? undefined}
      >
        {s.connected ? "Manage" : "Set up"}
      </button>
    );
  }
  // Request-only channel that we've wired up (admin set it active): the
  // "Connected" header pill says it all, so there's no action to offer.
  if (s.connected) return null;
  if (s.requested) {
    return (
      <button
        type="button"
        className={styles.btnGhost}
        data-testid={`channel-cancel-${def.slug}`}
        onClick={() => {
          void s.onCancelRequest();
        }}
      >
        Cancel request
      </button>
    );
  }
  return (
    <button
      type="button"
      className={styles.btnRequest}
      data-testid={`channel-action-${def.slug}`}
      onClick={() => {
        void s.onRequest();
      }}
    >
      Request access
    </button>
  );
};
